import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "my-x6 text-center" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center justify-start mt-x6 -mb-20"
}
const _hoisted_3 = { class: "ml-10" }
const _hoisted_4 = {
  key: 0,
  class: "w-full text-center"
}
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 0,
  class: "flex"
}
const _hoisted_7 = { class: "mt-20" }
const _hoisted_8 = { class: "flex justify-center" }

import { useCheckout, getCustomerAddressSuggestionByEmailMessageId } from '@/storecontext/checkout/useCheckout';
import { computed, ref, watch } from 'vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import {
    AddInvoiceAddressToBasketRequest,
    BasketCustomerInformation,
    CustomerAddressSuggestionModel,
    InvoiceAddressValidationRule,
} from '@/storecontext/shared/api/server-apis';
import useCountries from '@/shared/address/useCountries';
import { useDeliverables } from '@/storecontext/basket/useDeliverables';
import CustomerSuggest from '@/storecontext/shared/suggest/ui/CustomerSuggest.vue';
import { useRequiredForm } from '@/shared/form/useRequiredForm';
import { isEmpty, pickBy } from 'lodash';
import { defineRule } from 'vee-validate';
import { BaseAddressFormValues, CustomerType } from '@/storecontext/shared/address/ui/base-address-form.types';
import BaseAddressForm from '@/storecontext/shared/address/ui/BaseAddressForm.vue';
import CheckoutPaymentOptions from '@/storecontext/checkout/ui/payment/CheckoutPaymentOptions.vue';
import TermsAndConditions from '@/storecontext/shared/checkout/ui/TermsAndConditions.vue';
import siteContextService from '@/storecontext/shared/site-context.service';
import WebApiMessage from '@/core/http/WebApiMessage.vue';
import BasketDeliverableDialog from '@/storecontext/basket/ui/BasketDeliverableDialog.vue';
import useBooleanToggle from '@/shared/useBooleanToggle';
import HttpStatus from 'http-status-codes';
import Deferred from '@/core/async/Deferred';


export default /*#__PURE__*/_defineComponent({
  __name: 'CheckoutStepAddress',
  setup(__props) {

const labels = useBlockLabels();
const {
    isToggled: showMemberDiscountNotification,
    toggle: toggleMemberDiscountNotification,
} = useBooleanToggle();

let deferredMemberDiscountNotification: Deferred<void> | undefined;

const {
    updateStep,
    isLastStep,
    basket,
    deliverableCount,
    addInvoiceAddress,
    invoiceAddress,
    isAddressStep,
    selectedPaymentOption,
    setPaymentAddressValidationHandler,
    setIsPaymentReady,
    getCustomerAddressSuggestionByEmail,
    toggleCustomerMyBoliaSubscription,
} = useCheckout();
const { customerDeliveryPostalCode } = useDeliverables();
const {
    siteCountryCode,
    siteCountryName,
} = useCountries();

const pending = ref<boolean>(false);
const signupForAccountModel = ref<boolean>();

const resetToAddressStep = () => {
    updateStep(0);
};

const buyer = computed(() => {
    return invoiceAddress.value?.buyer;
});
const address = computed(() => {
    return invoiceAddress.value?.address;
});
const buyerContactInformation = computed(() => {
    return invoiceAddress.value?.buyerContactInformation;
});

const validationSchema = computed(dynamicValidationSchema);
const postalCodeValidationRegEx = computed<string>(() => siteContextService.country.postalCodeRegex);

const customerInformation = ref<BasketCustomerInformation|undefined>();
const previouslyValidatedEmail = ref<string|undefined>();

defineRule('validateExistingMemberByEmail', async(value) => {
    const emailHasChangedSinceLastValidation = value !== previouslyValidatedEmail.value;
    if (emailHasChangedSinceLastValidation) {
        try {
            previouslyValidatedEmail.value = value;
            const response = await getCustomerAddressSuggestionByEmail(value, 'validateExistingMemberByEmailCancellationKey');
            if (response) {
                const accountId = response.accountId || '';
                const isMyBoliaUser = response.isMyBoliaUser;
                customerInformation.value = {
                    accountId,
                    isMyBoliaUser,
                };
            } else {
                customerInformation.value = undefined;
            }
            return true;
        } catch (error: any) {
            if (error && error.status === HttpStatus.BAD_REQUEST) {
                customerInformation.value = undefined;
            }
            return true;
        }
    }
    return true;
});

watch(() => customerInformation.value?.isMyBoliaUser ?? false, (isMember: boolean) => {
    toggleCustomerMyBoliaSubscription(true);
    if (!isMember && signupForAccountModel.value) {
        enableNewsletter();
    }
});

const onSignupForAccountChange = (): void => {
    if (!customerInformation.value?.isMyBoliaUser && signupForAccountModel.value) {
        enableNewsletter();
        toggleCustomerMyBoliaSubscription(true);
    }
};

const resolveDeferredMemberDiscountNotification = () => {
    if (deferredMemberDiscountNotification) {
        deferredMemberDiscountNotification.resolve();
    }
};

const acceptSignupForAccountChange = async(): void => {
    signupForAccountModel.value = true;
    enableNewsletter();
    toggleMemberDiscountNotification();
    const validationResult = await validate({ mode: 'silent' });
    if (validationResult.valid) {
        setTimeout(resolveDeferredMemberDiscountNotification, 100);
    } else {
        deferredMemberDiscountNotification && deferredMemberDiscountNotification.reject();
        submit();
    }
};

const cancelSignupForAccountChange = (): void => {
    const timingForUserToNoticePriceSummaryUpdate = isLastStep.value ? 1500 : 100;
    toggleCustomerMyBoliaSubscription(false);
    toggleMemberDiscountNotification();
    setTimeout(resolveDeferredMemberDiscountNotification, timingForUserToNoticePriceSummaryUpdate);
};

const dismissSignupForAccountChange = (): void => {
    toggleMemberDiscountNotification();
    deferredMemberDiscountNotification && deferredMemberDiscountNotification.reject();
};

const initialValues = (): Partial<BaseAddressFormValues> => ({
    companyName: '',
    vatNumber: '',
    ...buyer.value,
    ...address.value,
    ...buyerContactInformation.value,
    country: siteCountryName.value,
    extraInformation: address.value?.street2,
    postalCode: address.value?.postalCode || customerDeliveryPostalCode.value || '',
    alternativePhoneNumber: buyerContactInformation.value?.alternateNumber,
    signupForAccount: invoiceAddress.value?.signUpMyBolia,
    subscribeToNewsletter: invoiceAddress.value?.signUpNewsLetter,
    email: invoiceAddress.value?.buyerContactInformation?.emailAddress,
});

function customerSelected(customerData: Partial<CustomerAddressSuggestionModel>) {
    resetForm();
    setValues({
        ...customerData,
        ...customerData.address,
        phoneNumber: customerData.phoneNumber || '',
        alternativePhoneNumber: customerData.alternativePhoneNumber || '',
        countryCode: siteCountryCode.value,
        country: siteCountryName.value,
        customerType: CustomerType.B2C,
    });
}

const gotoDeliverables = (): void => {
    updateStep(1);
};

const {
    handleSubmit,
    values,
    setValues,
    setFieldValue,
    validate,
    resetForm,
} = useRequiredForm({
    validationSchema,
    initialValues: initialValues(),
});

const disableNewsletter = computed<boolean>(() => {
    const isMyBoliaUser = customerInformation.value?.isMyBoliaUser ?? false;
    return values.signupForAccount && !isMyBoliaUser;
});

function enableNewsletter() {
    if (!values.subscribeToNewsletter) {
        setFieldValue('subscribeToNewsletter', true);
    }
}

function dynamicValidationSchema() {
    const data = values ?? {};
    const hasSomeValue = (...args) => {
        return args.some(arg => !!arg);
    };

    const rule = selectedPaymentOption.value?.addressValidationRule ?? basket.value.invoiceAddressValidationRule;
    const requiredByRule = rule === InvoiceAddressValidationRule.AllRequired;
    const requiredByOptionalRule = rule === InvoiceAddressValidationRule.NameAndEmailRequired || hasSomeValue(data.firstName, data.lastName, data.email, data.subscribeToNewsletter, data.signupForAccount);
    const isNewsletterSignupRequired = hasSomeValue(data.signupForAccount);

    const hasAnyNameInfo = hasSomeValue(data.firstName, data.lastName);
    const hasAnyAddressInfo = hasSomeValue(data.postalCode, data.city, data.street, data.hasLift);

    return {
        companyName: {
            max: 60,
        },
        firstName: {
            required: requiredByRule || requiredByOptionalRule || hasAnyNameInfo || hasAnyAddressInfo,
            max: 25,
        },
        lastName: {
            required: requiredByRule || requiredByOptionalRule || hasAnyNameInfo || hasAnyAddressInfo,
            max: 25,
        },
        postalCode: {
            postalCode: postalCodeValidationRegEx.value,
            required: requiredByRule || hasAnyAddressInfo,
        },
        city: {
            required: requiredByRule || hasAnyAddressInfo,
        },
        street: {
            required: requiredByRule || hasAnyAddressInfo,
        },
        country: {
            required: true,
        },
        phoneNumber: {
            required: requiredByRule,
            phone: true,
        },
        alternativePhoneNumber: {
            phone: true,
        },
        email: {
            required: requiredByRule || requiredByOptionalRule,
            email: true,
            validateExistingMemberByEmail: true,
        },
        subscribeToNewsletter: {
            required: isNewsletterSignupRequired,
        },
    };
}

// Wait for country names to be loaded
watch(siteCountryName, (name) => {
    setFieldValue('country', name);
}, { immediate: true });

const submit = handleSubmit(async(form) => {
    if (pending.value) {
        return;
    }
    pending.value = true;

    try {
        await maybeShowMembershipNotification();
        await addInvoiceAddress(getPaymentHandlerRequestValues());
        customerDeliveryPostalCode.value = form.postalCode ?? '';
        if (isLastStep.value) {
            setIsPaymentReady(true);
        } else {
            gotoDeliverables();
        }
    } catch (err) {
        setIsPaymentReady(false);
    } finally {
        pending.value = false;
    }
});

const maybeShowMembershipNotification = async() => {
    deferredMemberDiscountNotification = new Deferred<void>();
    const isMember = customerInformation.value?.isMyBoliaUser ?? false;
    const isRejectingNewMembershipDiscount = basket.value.hasMyBoliaBoostCampaignDiscount && !signupForAccountModel.value;

    if (isRejectingNewMembershipDiscount && !isMember) {
        toggleMemberDiscountNotification();
    } else {
        deferredMemberDiscountNotification.resolve();
    }
    return deferredMemberDiscountNotification.promise;
};

const paymentHandler = async() => {
    const result = await validate({ mode: 'silent' });
    if (result.valid) {
        await submit();
    } else {
        resetToAddressStep();
    }
    return result.valid;
};

setPaymentAddressValidationHandler(paymentHandler);

function getPaymentHandlerRequestValues() {
    const form = values;
    const buyer = {
        firstName: form.firstName || '',
        lastName: form.lastName || '',
        companyName: form.companyName,
        vatNumber: form.vatNumber,
    };

    const address = {
        city: form.city || '',
        hasLift: form.hasLift,
        otherCountryName: '',
        postalCode: form.postalCode || '',
        unknownPostalCode: form.isUnknownPostalCode,
        street: form.street || '',
        street2: form.extraInformation,
    };

    const buyerContactInformation = {
        emailAddress: form.email,
        phoneNumber: form.phoneNumber,
        alternateNumber: form.alternativePhoneNumber,
    };

    const hasBuyerInfo = !isEmpty(pickBy(buyer));
    const hasAddressInfo = !isEmpty(pickBy(address));
    const hasContactInfo = !isEmpty(pickBy(buyerContactInformation));

    const request: AddInvoiceAddressToBasketRequest = {
        invoiceAddress: {
            buyer: hasBuyerInfo ? buyer : undefined,
            address: hasAddressInfo ? {
                ...address,
                country: siteCountryName.value,
                countryCode: siteCountryCode.value,
            } : undefined,
            buyerContactInformation: hasContactInfo ? buyerContactInformation : undefined,
            customerInformation: customerInformation.value,
            signUpMyBolia: form.signupForAccount,
            signUpNewsLetter: form.subscribeToNewsletter,
        },
        validationRule: selectedPaymentOption.value?.addressValidationRule ?? basket.value.invoiceAddressValidationRule,
    };
    return request;
};


return (_ctx: any,_cache: any) => {
  const _component_c_icon = _resolveComponent("c-icon")!
  const _component_InputCheckbox = _resolveComponent("InputCheckbox")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("h1", _hoisted_1, _toDisplayString(_unref(labels).addressHeader), 1),
    _createElementVNode("form", {
      id: "checkoutStepAddress",
      name: "checkoutStepAddress",
      novalidate: "",
      onSubmit: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(submit) && _unref(submit)(...args))),
      onInput: resetToAddressStep
    }, [
      _createVNode(CustomerSuggest, {
        label: _unref(labels).customerSuggestLookup,
        "searching-text": _unref(labels).customerSuggestSearching,
        "no-results-text": _unref(labels).customerSuggestNoResults,
        class: "mb-x4",
        onSelected: customerSelected
      }, null, 8, ["label", "searching-text", "no-results-text"]),
      _createVNode(BaseAddressForm, {
        class: "mb-x8",
        "disable-newsletter": _unref(disableNewsletter),
        onInput: resetToAddressStep
      }, {
        signupForAccount: _withCtx(() => [
          _createVNode(_Transition, {
            name: "o-anim-fade-200",
            mode: "out-in"
          }, {
            default: _withCtx(() => [
              (customerInformation.value?.isMyBoliaUser ?? false)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createVNode(_component_c_icon, {
                      name: "icon-checkmark",
                      class: "h-15"
                    }),
                    _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(labels).invoiceAddressIsMyBoliaUser), 1)
                  ]))
                : (_openBlock(), _createBlock(_component_InputCheckbox, {
                    key: 1,
                    modelValue: signupForAccountModel.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((signupForAccountModel).value = $event)),
                    name: "signupForAccount",
                    class: "mt-x6 -mb-20",
                    "show-errors": false,
                    label: _unref(labels).invoiceAddressSignupForAccount,
                    onInput: onSignupForAccountChange
                  }, null, 8, ["modelValue", "label"]))
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["disable-newsletter"]),
      _createVNode(WebApiMessage, {
        id: _unref(getCustomerAddressSuggestionByEmailMessageId),
        class: "hidden",
        "aria-hidden": "true"
      }, null, 8, ["id"]),
      _createVNode(_Transition, { name: "o-anim-fade-300" }, {
        default: _withCtx(() => [
          (_unref(isAddressStep) && !_unref(isLastStep))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("button", {
                  type: "submit",
                  class: "c-button c-button--primary",
                  disabled: pending.value
                }, _toDisplayString(_ctx.$dictionary.pluralize(_unref(deliverableCount), _unref(labels).continueToDeliverablesButton, _unref(labels).continueToDeliverablesButtonPlural)), 9, _hoisted_5)
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ], 32),
    (_unref(isAddressStep) && _unref(isLastStep))
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(TermsAndConditions, {
            label: _unref(labels).selectTermsAndConditions,
            class: "mr-auto min-w-180 mb-x3 mb:mb-0"
          }, null, 8, ["label"]),
          _createVNode(CheckoutPaymentOptions, { class: "md:ml-x2 u-valign-inline-button" })
        ]))
      : _createCommentVNode("", true),
    _createVNode(BasketDeliverableDialog, {
      "is-open": _unref(showMemberDiscountNotification),
      class: "lg:max-w-700 xl:max-w-850",
      onClose: dismissSignupForAccountChange
    }, {
      heading: _withCtx(() => [
        _createElementVNode("h2", _hoisted_7, _toDisplayString(_unref(labels).invoiceAddressSignupForAccountNotification), 1)
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("button", {
            type: "button",
            class: "c-button c-button--outline",
            onClick: cancelSignupForAccountChange
          }, _toDisplayString(_unref(labels).invoiceAddressSignupForAccountNotificationCancelButton), 1),
          _createElementVNode("button", {
            type: "button",
            class: "c-button c-button--primary ml-20",
            onClick: acceptSignupForAccountChange
          }, _toDisplayString(_unref(labels).invoiceAddressSignupForAccountNotificationAcceptButton), 1)
        ])
      ]),
      _: 1
    }, 8, ["is-open"])
  ]))
}
}

})